/**
 * AppShell — shared layout component used by all four PazPortal apps.
 *
 * Design decisions:
 *   - Accepts `onNavigate` + `currentPath` instead of depending on react-router-dom,
 *     keeping packages/ui router-agnostic. Nav anchors keep a real `href` so
 *     middle-click / cmd-click open in a new tab.
 *   - Sidebar uses portalColor (passed per-app). Main area uses the shared
 *     design tokens (var(--bg), var(--surface), …); dark mode is driven by
 *     `[data-theme="dark"]` on <html>.
 *   - The DOCUMENT scrolls (root is min-h-dvh, not a fixed-height overflow
 *     container). That is what makes momentum scrolling and the collapsing
 *     URL bar behave on iOS. Header and desktop sidebar are `sticky` and are
 *     offset by `--paz-shell-offset`, the measured height of the optional
 *     view-as banner, so nothing hides underneath it.
 *   - Below `md` the sidebar is a slide-in drawer: a labelled modal dialog,
 *     `inert` + aria-hidden + invisible while closed (so it is out of the tab
 *     order even in browsers without `inert`), Escape closes, focus moves in on
 *     open and returns to the hamburger on close, and the main column is made
 *     `inert` while the drawer is open (that is the focus trap).
 *   - Touch targets are ≥ 44px below `md` and shrink to the dense desktop
 *     sizes at `md+`.
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface NavItem {
  label: string;
  href: string;
  icon?: React.ReactNode;
  /** Count shown as a pill. Not rendered when 0 / '' / undefined. */
  badge?: string | number;
  badgeVariant?: 'error' | 'warning' | 'info' | 'success';
  /** Screen-reader context for the badge count ("3 open"). Defaults to "open". */
  badgeLabel?: string;
  children?: NavItem[];
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

export interface AppShellProps {
  navSections: NavSection[];
  portalColor: string;
  portalName: string;
  portalSubtitle: string;
  propertyName: string;
  propertySubtitle: string;
  propertyInitials: string;
  alertCount?: number;
  userName?: string;
  userRole?: string;
  userInitials?: string;
  /** Optional image for the user icon (e.g. the portfolio owner's mark); falls back to initials. */
  userAvatarSrc?: string;
  /** Alt text for userAvatarSrc. */
  userAvatarAlt?: string;
  /**
   * Optional brand block rendered at the top of the sidebar, above the
   * navigation search: a wordmark (white on the sidebar colour) with the
   * property name beneath it. The customer portal sets it when a single
   * branded property is selected.
   */
  brandHeader?: { logoSrc?: string; logoAlt: string; title: string };
  /**
   * The portal's own logo, shown in place of the text name at the top of the
   * sidebar. Optional, so apps that have not adopted it keep the text header.
   * Pass it only when the sidebar is dark enough for the logo to read.
   */
  portalLogoSrc?: string;
  /**
   * An interactive account control for the top bar. When given, it replaces the
   * read-only property chip, so the page does not show the same account twice
   * (once as a chip and once as a separate switcher bar).
   */
  accountControl?: React.ReactNode;
  currentPath: string;
  onNavigate: (href: string) => void;
  onSignOut?: () => void;
  onDarkModeToggle?: () => void;
  isDarkMode?: boolean;
  /**
   * Sticky, full-width bar rendered above the sidebar and header
   * (the customer portal uses it for the staff "viewing as" banner).
   */
  viewAsBanner?: React.ReactNode;
  /** Extra controls rendered in the header, right of the search box. */
  headerActions?: React.ReactNode;
  /**
   * Optional app-level search. When the header search query matches no
   * navigation item and the user presses Enter, this receives the query.
   * Without it the header search is a navigation finder only.
   */
  onGlobalSearch?: (query: string) => void;
  children: React.ReactNode;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DESKTOP_QUERY = '(min-width: 768px)';
const MAX_SEARCH_RESULTS = 8;

const badgeColors: Record<string, string> = {
  error: 'bg-error text-text-inverse',
  warning: 'bg-warning text-text-inverse',
  info: 'bg-info text-text-inverse',
  success: 'bg-success text-text-inverse',
};

const SIDEBAR_FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--sb-text-80)]';
const HEADER_FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Same prefix logic for parents and children: `/tickets` matches `/tickets`
 * and `/tickets/123` but not `/ticketsx`. The root href only matches the root.
 */
export function navPathMatches(currentPath: string, href: string): boolean {
  if (!href) return false;
  const base = href.length > 1 && href.endsWith('/') ? href.slice(0, -1) : href;
  if (base === '/') return currentPath === '/';
  return currentPath === base || currentPath.startsWith(base + '/');
}

/** Longest matching nav href, so a site dashboard does not stay lit on that site's tickets page. */
export function winningNavHref(sections: NavSection[], currentPath: string): string | undefined {
  const hrefs: string[] = [];
  for (const section of sections) {
    for (const item of section.items) {
      if (item.href) hrefs.push(item.href);
      for (const child of item.children ?? []) {
        if (child.href) hrefs.push(child.href);
      }
    }
  }
  return hrefs
    .filter((href) => navPathMatches(currentPath, href))
    .sort((a, b) => b.length - a.length)[0];
}

/** Header height (must match the `h-11` on <header>), exposed to pages as --paz-header-offset. */
const HEADER_HEIGHT = '2.75rem';

/**
 * React 18 has no typed `inert` prop. An empty string sets the boolean
 * attribute; `undefined` removes it. (Boolean `true` would be dropped with a
 * warning.)
 */
function inertProps(inert: boolean): Record<string, string | undefined> {
  return { inert: inert ? '' : undefined };
}

function hasBadge(badge: NavItem['badge']): badge is string | number {
  return badge != null && badge !== '' && badge !== 0 && badge !== '0';
}

/** Non-primary clicks (new tab, etc.) are left to the browser. */
function isModifiedClick(e: React.MouseEvent): boolean {
  return e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(mq.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [query]);

  return matches;
}

interface SearchEntry {
  key: string;
  label: string;
  href: string;
  section: string;
  parent?: string;
}

function flattenNav(sections: NavSection[]): SearchEntry[] {
  const entries: SearchEntry[] = [];
  for (const section of sections) {
    for (const item of section.items) {
      entries.push({ key: `${section.label}|${item.href}`, label: item.label, href: item.href, section: section.label });
      for (const child of item.children ?? []) {
        entries.push({
          key: `${section.label}|${item.href}|${child.href}`,
          label: child.label,
          href: child.href,
          section: section.label,
          parent: item.label,
        });
      }
    }
  }
  return entries;
}

function includesQuery(text: string | undefined, q: string): boolean {
  return !!text && text.toLowerCase().includes(q);
}

/** Parents whose child matches the current route — expanded on first paint, not after an effect. */
function parentsOfCurrentPath(sections: NavSection[], currentPath: string): Set<string> {
  const keys = new Set<string>();
  for (const section of sections) {
    for (const item of section.items) {
      if (item.children?.some((child) => navPathMatches(currentPath, child.href))) keys.add(item.href);
    }
  }
  return keys;
}

/** Sidebar filter: keeps an item when it or any child matches; narrows children otherwise. */
function filterSections(sections: NavSection[], query: string): NavSection[] {
  const q = query.trim().toLowerCase();
  if (!q) return sections;
  return sections
    .map((section) => ({
      ...section,
      items: section.items.flatMap((item) => {
        if (includesQuery(item.label, q)) return [item];
        const children = (item.children ?? []).filter((c) => includesQuery(c.label, q));
        return children.length > 0 ? [{ ...item, children }] : [];
      }),
    }))
    .filter((section) => section.items.length > 0);
}

// ─── Component ───────────────────────────────────────────────────────────────

export function AppShell({
  navSections,
  portalColor,
  portalName,
  portalSubtitle,
  propertyName,
  propertySubtitle,
  propertyInitials,
  alertCount,
  userName,
  userRole,
  userInitials,
  userAvatarSrc,
  userAvatarAlt,
  brandHeader,
  portalLogoSrc,
  accountControl,
  currentPath,
  onNavigate,
  onSignOut,
  onDarkModeToggle,
  isDarkMode,
  viewAsBanner,
  headerActions,
  onGlobalSearch,
  children,
}: AppShellProps) {
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const activeHref = useMemo(
    () => winningNavHref(navSections, currentPath),
    [navSections, currentPath],
  );

  const [expandedItems, setExpandedItems] = useState<Set<string>>(() =>
    parentsOfCurrentPath(navSections, currentPath),
  );
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  const [headerQuery, setHeaderQuery] = useState('');
  const [headerOpen, setHeaderOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const rootRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  const drawerId = useId();
  const sidebarSearchId = useId();
  const headerSearchId = useId();
  const listboxId = useId();

  const hasBanner = viewAsBanner != null && viewAsBanner !== false;
  const drawerHidden = !isDesktop && !mobileOpen;
  const mainInert = !isDesktop && mobileOpen;

  // ── Navigation ─────────────────────────────────────────────────────────────

  const navigate = useCallback(
    (href: string) => {
      onNavigate(href);
      setMobileOpen(false);
    },
    [onNavigate],
  );

  const toggleExpand = useCallback((key: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Keep the parent of the current route expanded as the route changes.
  useEffect(() => {
    setExpandedItems((prev) => {
      let next: Set<string> | null = null;
      for (const key of parentsOfCurrentPath(navSections, currentPath)) {
        if (prev.has(key)) continue;
        next ??= new Set(prev);
        next.add(key);
      }
      return next ?? prev;
    });
  }, [currentPath, navSections]);

  // Close drawer on route change (defensive — navigate() already covers this)
  useEffect(() => {
    setMobileOpen(false);
  }, [currentPath]);

  // The drawer only exists below md
  useEffect(() => {
    if (isDesktop) setMobileOpen(false);
  }, [isDesktop]);

  // ── Drawer: scroll lock, Escape, focus management ──────────────────────────

  useEffect(() => {
    if (!mobileOpen || isDesktop) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen, isDesktop]);

  useEffect(() => {
    if (!mobileOpen || isDesktop) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen, isDesktop]);

  useEffect(() => {
    if (isDesktop) {
      wasOpenRef.current = false;
      return;
    }
    if (mobileOpen) {
      wasOpenRef.current = true;
      const frame = window.requestAnimationFrame(() => {
        drawerRef.current?.focus({ preventScroll: true });
      });
      return () => window.cancelAnimationFrame(frame);
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      hamburgerRef.current?.focus({ preventScroll: true });
    }
  }, [mobileOpen, isDesktop]);

  // ── View-as banner offset (sticky header/sidebar sit below it) ─────────────

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const banner = bannerRef.current;
    if (!hasBanner || !banner) {
      root.style.setProperty('--paz-shell-offset', '0px');
      return;
    }
    const update = () => root.style.setProperty('--paz-shell-offset', `${banner.offsetHeight}px`);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(banner);
    return () => observer.disconnect();
  }, [hasBanner]);

  // ── Header search (combobox over nav items incl. children) ─────────────────

  const searchEntries = useMemo(() => flattenNav(navSections), [navSections]);
  const searchResults = useMemo(() => {
    const q = headerQuery.trim().toLowerCase();
    if (!q) return [];
    return searchEntries
      .filter(
        (entry) =>
          includesQuery(entry.label, q) || includesQuery(entry.parent, q) || includesQuery(entry.section, q),
      )
      .slice(0, MAX_SEARCH_RESULTS);
  }, [searchEntries, headerQuery]);

  const listOpen = headerOpen && headerQuery.trim().length > 0;
  const highlighted = activeIndex >= 0 && activeIndex < searchResults.length ? activeIndex : -1;

  const goToResult = useCallback(
    (href: string) => {
      setHeaderQuery('');
      setHeaderOpen(false);
      setActiveIndex(-1);
      navigate(href);
    },
    [navigate],
  );

  const onHeaderSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        if (searchResults.length === 0) return;
        setHeaderOpen(true);
        setActiveIndex((i) => (i < 0 ? 0 : Math.min(i + 1, searchResults.length - 1)));
        return;
      }
      case 'ArrowUp': {
        event.preventDefault();
        if (searchResults.length === 0) return;
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      case 'Enter': {
        const target = highlighted >= 0 ? searchResults[highlighted] : searchResults[0];
        if (target) {
          event.preventDefault();
          goToResult(target.href);
          return;
        }
        const query = headerQuery.trim();
        if (onGlobalSearch && query) {
          event.preventDefault();
          setHeaderOpen(false);
          onGlobalSearch(query);
        }
        return;
      }
      case 'Escape': {
        if (listOpen) {
          event.preventDefault();
          setHeaderOpen(false);
          setActiveIndex(-1);
        } else if (headerQuery) {
          event.preventDefault();
          setHeaderQuery('');
        }
        return;
      }
      default:
        return;
    }
  };

  const visibleSections = useMemo(() => filterSections(navSections, sidebarSearch), [navSections, sidebarSearch]);
  const sidebarFiltering = sidebarSearch.trim().length > 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  // Root min-height uses --paz-vh: 100vh, upgraded to 100dvh under @supports
  // in globals.css (Tailwind emits .min-h-dvh BEFORE .min-h-screen, so the
  // two-class fallback pattern does not work).

  // --paz-header-offset lets pages stick things below the shell header + banner:
  //   className="sticky top-[var(--paz-header-offset)]"
  const rootStyle = {
    backgroundColor: 'var(--bg)',
    '--paz-header-offset': `calc(var(--paz-shell-offset, 0px) + ${HEADER_HEIGHT})`,
  } as React.CSSProperties;

  return (
    <div ref={rootRef} className="flex min-h-[var(--paz-vh,100vh)] flex-col" style={rootStyle}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[70] focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:text-text-inverse"
      >
        Skip to content
      </a>

      {hasBanner && (
        // Inert while the drawer is open so Tab cannot escape the modal into the banner
        <div ref={bannerRef} className="sticky top-0 z-[60] w-full" {...inertProps(mainInert)}>
          {viewAsBanner}
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* ─── Mobile backdrop ──────────────────────────────────────────── */}
        {mobileOpen && !isDesktop && (
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* ─── Sidebar / drawer ─────────────────────────────────────────── */}
        <aside
          ref={drawerRef}
          id={drawerId}
          tabIndex={-1}
          role={isDesktop ? undefined : 'dialog'}
          aria-modal={isDesktop ? undefined : true}
          aria-label={`${portalName} navigation`}
          aria-hidden={drawerHidden || undefined}
          {...inertProps(drawerHidden)}
          className={cn(
            'flex w-64 flex-shrink-0 flex-col overflow-y-auto md:w-52',
            'fixed bottom-0 left-0 z-50 transform transition-[transform,visibility] duration-200 ease-out',
            'md:sticky md:bottom-auto md:left-auto md:z-auto md:translate-x-0 md:self-start md:transition-none',
            mobileOpen ? 'translate-x-0 visible' : '-translate-x-full invisible md:visible',
            'pl-[env(safe-area-inset-left)] pb-[env(safe-area-inset-bottom)]',
            'focus:outline-none',
          )}
          style={
            {
              backgroundColor: portalColor,
              color: 'var(--sidebar-text, #ffffff)',
              // Sidebar text tokens: brand themes may set --sidebar-text (e.g. black on a
              // white Marriott sidebar); every tint below derives from it.
              '--sb-text': 'var(--sidebar-text, #ffffff)',
              '--sb-text-80': 'color-mix(in srgb, var(--sidebar-text, #ffffff) 80%, transparent)',
              '--sb-text-70': 'color-mix(in srgb, var(--sidebar-text, #ffffff) 70%, transparent)',
              '--sb-text-50': 'color-mix(in srgb, var(--sidebar-text, #ffffff) 50%, transparent)',
              '--sb-text-30': 'color-mix(in srgb, var(--sidebar-text, #ffffff) 30%, transparent)',
              '--sb-text-20': 'color-mix(in srgb, var(--sidebar-text, #ffffff) 20%, transparent)',
              '--sb-text-15': 'color-mix(in srgb, var(--sidebar-text, #ffffff) 15%, transparent)',
              '--sb-text-10': 'color-mix(in srgb, var(--sidebar-text, #ffffff) 10%, transparent)',
              '--sb-text-5': 'color-mix(in srgb, var(--sidebar-text, #ffffff) 5%, transparent)',
              top: 'var(--paz-shell-offset, 0px)',
              height: 'calc(var(--paz-vh, 100vh) - var(--paz-shell-offset, 0px))',
            } as React.CSSProperties
          }
        >
          {/* Portal header */}
          <div className="flex items-start justify-between px-3 pb-2 pt-4">
            <div className="min-w-0">
              <h1 className="truncate text-sm font-bold leading-tight text-[var(--sb-text)]">
                {portalLogoSrc ? (
                  <img src={portalLogoSrc} alt={portalName} className="h-7 w-auto max-w-full object-contain object-left" />
                ) : (
                  portalName
                )}
              </h1>
              <p className="mt-0.5 truncate text-[10px] text-[var(--sb-text-70)]">{portalSubtitle}</p>
            </div>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className={cn(
                '-mr-2 -mt-2 flex h-11 w-11 items-center justify-center rounded-md text-[var(--sb-text-80)] hover:bg-[var(--sb-text-10)] hover:text-[var(--sb-text)] md:hidden',
                SIDEBAR_FOCUS_RING,
              )}
              aria-label="Close navigation"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Brand header — wordmark + property name, above the search */}
          {brandHeader && (
            <div
              className="mx-3 mb-2 pb-3 pt-1"
              data-testid="sidebar-brand-header"
              style={{ borderBottom: '2px solid var(--brand-accent, transparent)' }}
            >
              {brandHeader.logoSrc ? (
                <img src={brandHeader.logoSrc} alt={brandHeader.logoAlt} className="h-10 w-auto max-w-full object-contain object-left" />
              ) : (
                <p className="text-lg font-semibold leading-tight text-[var(--sb-text)]">{brandHeader.logoAlt}</p>
              )}
              <p className="mt-1 text-[10px] font-medium uppercase leading-snug tracking-[0.14em] text-[var(--sb-text)]">
                {brandHeader.title}
              </p>
            </div>
          )}

          {/* Sidebar search */}
          <div className="px-3 pb-2">
            <label htmlFor={sidebarSearchId} className="sr-only">
              Search navigation
            </label>
            <input
              id={sidebarSearchId}
              type="search"
              autoComplete="off"
              placeholder="Search navigation"
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)}
              className={cn(
                'h-11 w-full rounded border-none bg-[var(--sb-text-10)] px-2 text-sm text-[var(--sb-text)] outline-none transition-colors placeholder:text-[var(--sb-text-70)] focus:bg-[var(--sb-text-15)] md:h-7 md:text-xs',
                SIDEBAR_FOCUS_RING,
              )}
            />
          </div>

          {/* Navigation sections */}
          <nav className="flex-1 space-y-3 px-1 pb-2" aria-label="Primary">
            {visibleSections.map((section) => (
              <div key={section.label}>
                <p className="mb-1 px-2 text-[9px] font-semibold uppercase tracking-wider text-[var(--sb-text-70)]">
                  {section.label}
                </p>
                <ul className="space-y-0.5">
                  {section.items.map((item) => (
                    <NavItemRow
                      key={item.href + item.label}
                      item={item}
                      activeHref={activeHref}
                      expanded={sidebarFiltering || expandedItems.has(item.href)}
                      onToggle={() => toggleExpand(item.href)}
                      onNavigate={navigate}
                    />
                  ))}
                </ul>
              </div>
            ))}
            {sidebarFiltering && visibleSections.length === 0 && (
              <p className="px-3 py-2 text-xs text-[var(--sb-text-70)]" role="status">
                No pages match &ldquo;{sidebarSearch.trim()}&rdquo;
              </p>
            )}
          </nav>

          {/* User identity + sign out */}
          <div className="mt-auto flex items-center gap-2 border-t border-[var(--sb-text-10)] px-3 py-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {userAvatarSrc ? (
                <img
                  src={userAvatarSrc}
                  alt={userAvatarAlt ?? ''}
                  className="h-7 w-7 flex-shrink-0 rounded-full bg-[var(--sb-text-20)] object-contain p-0.5"
                />
              ) : (
                <div
                  className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--sb-text-20)] text-[10px] font-bold text-[var(--sb-text)]"
                  aria-hidden="true"
                >
                  {userInitials || '??'}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-[var(--sb-text)]">{userName || 'User'}</p>
                {userRole && <p className="truncate text-[10px] text-[var(--sb-text-70)]">{userRole}</p>}
              </div>
            </div>
            {onSignOut && (
              <button
                type="button"
                onClick={onSignOut}
                aria-label="Sign out"
                title="Sign out"
                className={cn(
                  'flex h-11 flex-shrink-0 items-center gap-1.5 rounded-md px-2 text-xs text-[var(--sb-text-80)] hover:bg-[var(--sb-text-10)] hover:text-[var(--sb-text)] md:h-8',
                  SIDEBAR_FOCUS_RING,
                )}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                <span>Sign out</span>
              </button>
            )}
          </div>
        </aside>

        {/* ─── Main column ──────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-1 flex-col" {...inertProps(mainInert)}>
          {/* Topbar */}
          <header
            className="sticky z-30 flex h-11 flex-shrink-0 items-center gap-1.5 px-2 sm:gap-2 sm:px-3"
            style={{
              top: 'var(--paz-shell-offset, 0px)',
              backgroundColor: 'var(--bg-elevated)',
              borderBottom: '1px solid var(--divider)',
            }}
          >
            {/* Hamburger (mobile only) */}
            <button
              ref={hamburgerRef}
              type="button"
              onClick={() => setMobileOpen(true)}
              className={cn(
                '-ml-2 flex h-11 w-11 items-center justify-center rounded-md transition-colors md:hidden',
                HEADER_FOCUS_RING,
              )}
              style={{ color: 'var(--text-muted)' }}
              aria-label="Open navigation"
              aria-expanded={mobileOpen}
              aria-controls={drawerId}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Page search — hidden on small phones (the drawer has its own search) */}
            <div className="relative hidden max-w-[260px] flex-1 sm:block">
              <label htmlFor={headerSearchId} className="sr-only">
                {onGlobalSearch ? 'Search everything' : 'Search pages'}
              </label>
              <input
                id={headerSearchId}
                type="search"
                role="combobox"
                autoComplete="off"
                aria-autocomplete="list"
                aria-haspopup="listbox"
                aria-expanded={listOpen}
                aria-controls={listboxId}
                aria-activedescendant={listOpen && highlighted >= 0 ? `${listboxId}-${highlighted}` : undefined}
                placeholder={onGlobalSearch ? 'Search everything…' : 'Search pages…'}
                value={headerQuery}
                onChange={(e) => {
                  setHeaderQuery(e.target.value);
                  setHeaderOpen(true);
                  setActiveIndex(-1);
                }}
                onFocus={() => setHeaderOpen(true)}
                onBlur={() => setHeaderOpen(false)}
                onKeyDown={onHeaderSearchKeyDown}
                className={cn('h-8 w-full rounded-md px-2.5 text-xs transition-colors md:h-7', HEADER_FOCUS_RING)}
                style={{
                  backgroundColor: 'var(--surface)',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              />
              {listOpen && (
                <div
                  className="absolute left-0 right-0 top-full z-40 mt-1 overflow-hidden rounded-md shadow-lg"
                  style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border)' }}
                >
                  {searchResults.length > 0 ? (
                    <ul role="listbox" id={listboxId} aria-label="Matching pages">
                      {searchResults.map((entry, index) => (
                        <li
                          key={entry.key}
                          id={`${listboxId}-${index}`}
                          role="option"
                          aria-selected={index === highlighted}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => goToResult(entry.href)}
                          className={cn(
                            'flex cursor-pointer flex-col px-2.5 py-1.5 text-xs',
                            index === highlighted ? 'bg-surface-2' : 'hover:bg-surface-2',
                          )}
                        >
                          <span className="text-text">{entry.parent ? `${entry.parent} › ${entry.label}` : entry.label}</span>
                          <span className="text-[10px] text-text-muted">{entry.section}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div role="status" className="px-2.5 py-2 text-xs text-text-muted">
                      {onGlobalSearch ? 'No matching pages. Press Enter to search everything.' : 'No matching pages.'}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {headerActions}

            {/* Alert pill */}
            {alertCount != null && alertCount > 0 && (
              <div
                className="flex items-center gap-1 rounded-full px-2 py-0.5"
                title={`${alertCount} open ${alertCount === 1 ? 'alert' : 'alerts'}`}
                style={{
                  backgroundColor: 'var(--notification-soft)',
                  border: '1px solid var(--notification)',
                }}
              >
                <svg
                  className="h-3 w-3"
                  style={{ color: 'var(--notification)' }}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
                <span className="text-[10px] font-bold" style={{ color: 'var(--notification)' }} aria-hidden="true">
                  {alertCount}
                </span>
                <span className="sr-only">{`${alertCount} open ${alertCount === 1 ? 'alert' : 'alerts'}`}</span>
              </div>
            )}

            {/* Dark mode toggle */}
            {onDarkModeToggle && (
              <button
                type="button"
                onClick={onDarkModeToggle}
                aria-label="Dark mode"
                aria-pressed={!!isDarkMode}
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                className={cn(
                  'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md transition-colors md:h-8 md:w-8',
                  HEADER_FOCUS_RING,
                )}
                style={{ color: 'var(--text-muted)' }}
              >
                {isDarkMode ? (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                    />
                  </svg>
                )}
              </button>
            )}

            {accountControl ?? (
              <>
              {/* Property chip — collapses to initials-only avatar on phones */}
              <div
                className="flex flex-shrink-0 items-center gap-1.5 rounded-md px-1 py-1 sm:px-2"
                style={{
                  backgroundColor: 'var(--surface)',
                  border: '1px solid var(--border)',
                }}
              >
                <div
                  className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-[9px] font-bold"
                  style={{
                    backgroundColor: 'var(--primary)',
                    color: 'var(--text-inverse)',
                  }}
                  aria-hidden="true"
                >
                  {propertyInitials}
                </div>
                <div className="hidden min-w-0 max-w-[160px] sm:block">
                  <p className="truncate text-[11px] font-medium leading-tight" style={{ color: 'var(--text)' }}>
                    {propertyName}
                  </p>
                  <p className="truncate text-[9px] leading-tight" style={{ color: 'var(--text-muted)' }}>
                    {propertySubtitle}
                  </p>
                </div>
                <span className="sr-only sm:hidden">
                  {propertyName}, {propertySubtitle}
                </span>
              </div>
              </>
            )}
          </header>

          {/* Content area */}
          <main
            id="main"
            tabIndex={-1}
            className="min-w-0 flex-1 p-3 focus:outline-none sm:p-4 lg:p-6"
            style={{ backgroundColor: 'var(--bg)' }}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}

// ─── NavItemRow (internal) ───────────────────────────────────────────────────

function NavItemRow({
  item,
  activeHref,
  expanded,
  onToggle,
  onNavigate,
}: {
  item: NavItem;
  activeHref: string | undefined;
  expanded: boolean;
  onToggle: () => void;
  onNavigate: (href: string) => void;
}) {
  const childListId = useId();
  const active = item.href === activeHref;
  const hasChildren = !!item.children && item.children.length > 0;

  const rowClass = cn(
    'flex w-full min-h-11 items-center gap-2 border-l-2 px-3 py-2 text-left text-sm transition-colors md:min-h-0 md:py-1.5 md:text-xs rounded-r-md',
    SIDEBAR_FOCUS_RING,
    active
      ? 'border-[var(--brand-accent,var(--sb-text))] bg-[var(--nav-highlight,rgba(255,255,255,0.1))] font-medium text-[var(--nav-highlight-text,var(--sb-text))]'
      : 'border-transparent text-[var(--sb-text-70)] hover:bg-[var(--sb-text-5)] hover:text-[var(--sb-text)]',
  );

  const content = (
    <>
      {item.icon && (
        <span className="flex h-4 w-4 items-center justify-center opacity-80" aria-hidden="true">
          {item.icon}
        </span>
      )}
      <span className="flex-1 truncate">{item.label}</span>
      {hasBadge(item.badge) && (
        <span
          className={cn(
            'rounded-full px-1.5 py-0.5 text-[9px] font-bold leading-none',
            badgeColors[item.badgeVariant || 'info'],
          )}
        >
          <span aria-hidden="true">{item.badge}</span>
          <span className="sr-only">{`${item.badge} ${item.badgeLabel ?? 'open'}`}</span>
        </span>
      )}
      {hasChildren && (
        <svg
          className={cn('h-3 w-3 text-[var(--sb-text-70)] transition-transform', expanded && 'rotate-90')}
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </>
  );

  return (
    <li>
      {hasChildren ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={childListId}
          aria-current={active ? 'page' : undefined}
          className={rowClass}
        >
          {content}
        </button>
      ) : (
        <a
          href={item.href}
          aria-current={active ? 'page' : undefined}
          onClick={(e) => {
            if (isModifiedClick(e)) return;
            e.preventDefault();
            onNavigate(item.href);
          }}
          className={rowClass}
        >
          {content}
        </a>
      )}

      {/* Collapsible children — invisible + inert while collapsed so they are not tabbable */}
      {hasChildren && (
        <ul
          id={childListId}
          aria-hidden={!expanded || undefined}
          {...inertProps(!expanded)}
          className={cn(
            'overflow-hidden transition-[max-height,opacity,visibility] duration-200 ease-in-out',
            expanded ? 'visible max-h-96 opacity-100' : 'invisible max-h-0 opacity-0',
          )}
        >
          {item.children!.map((child) => {
            const childActive = child.href === activeHref;
            return (
              <li key={child.href}>
                <a
                  href={child.href}
                  aria-current={childActive ? 'page' : undefined}
                  onClick={(e) => {
                    if (isModifiedClick(e)) return;
                    e.preventDefault();
                    onNavigate(child.href);
                  }}
                  className={cn(
                    'flex w-full min-h-11 items-center gap-2 border-l-2 pl-9 pr-3 py-2 text-[13px] transition-colors md:min-h-0 md:py-1 md:text-[11px] rounded-r-md',
                    SIDEBAR_FOCUS_RING,
                    childActive
                      ? 'border-[var(--brand-accent,var(--sb-text))] bg-[var(--nav-highlight,rgba(255,255,255,0.1))] text-[var(--nav-highlight-text,var(--sb-text))]'
                      : 'border-transparent text-[var(--sb-text-70)] hover:bg-[var(--sb-text-5)] hover:text-[var(--sb-text)]',
                  )}
                >
                  <span className="truncate">{child.label}</span>
                  {hasBadge(child.badge) && (
                    <span
                      className={cn(
                        'ml-auto rounded-full px-1.5 py-0.5 text-[9px] font-bold leading-none',
                        badgeColors[child.badgeVariant || 'info'],
                      )}
                    >
                      <span aria-hidden="true">{child.badge}</span>
                      <span className="sr-only">{`${child.badge} ${child.badgeLabel ?? 'open'}`}</span>
                    </span>
                  )}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
